import {
  getGovernmentJurisdictionOptions,
  getGovernmentLicenseTypeConfigurationsOptions,
  getInspectionsOptions,
  getNonRegistrantLicenseStatsOptions,
  getUnregisteredPropertiesOptions,
} from "@/hera-api/client/@tanstack/react-query.gen";
import {
  DASHBOARD_SECTIONS_SKIP_INSPECTIONS,
  DASHBOARD_SECTIONS_SKIP_LICENSE_STATS,
  DASHBOARD_SECTIONS_SKIP_REMITTANCE,
  DASHBOARD_SECTIONS_SKIP_UNREGISTERED,
  dashboardListQueryOptions,
} from "@/lib/dashboard-query-options";
import { buildOrdinanceRowsFromGovernmentData } from "@/lib/ordinance-list-rows";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { regionQueryParams } from "@/hera-api/regions-api";

import { useStreamingNdjson } from "./use-streaming-ndjson";

export function useGovernmentDashboardStats(
  isAuthSetupComplete: boolean,
  selectedLicenseTypes: string[],
  activeSection: string,
  selectedRegionIds: string[] = [],
) {
  type RemittanceTrendRow = {
    bucket: string;
    hera_registration_remittance: number;
    hera_late_fee_remittance: number;
    hera_inspection_remittance: number;
  };
  // Get the current government user's jurisdiction
  const {
    data: jurisdictionData,
    isLoading: isJurisdictionQueryLoading,
    error: jurisdictionQueryError,
  } = useQuery({
    ...getGovernmentJurisdictionOptions(),
    enabled: isAuthSetupComplete,
    ...dashboardListQueryOptions,
  });

  // Get license type configurations for the government user's jurisdiction
  const { data: licenseTypeConfigsData } = useQuery({
    ...getGovernmentLicenseTypeConfigurationsOptions(),
    enabled: isAuthSetupComplete,
    ...dashboardListQueryOptions,
  });

  const loadLicenseStats =
    isAuthSetupComplete &&
    !!jurisdictionData &&
    !DASHBOARD_SECTIONS_SKIP_LICENSE_STATS.has(activeSection);
  const loadUnregistered =
    isAuthSetupComplete &&
    !!jurisdictionData &&
    !DASHBOARD_SECTIONS_SKIP_UNREGISTERED.has(activeSection);
  const loadInspections =
    isAuthSetupComplete &&
    !!jurisdictionData &&
    !DASHBOARD_SECTIONS_SKIP_INSPECTIONS.has(activeSection);
  const loadRemittance =
    isAuthSetupComplete &&
    !!jurisdictionData &&
    !DASHBOARD_SECTIONS_SKIP_REMITTANCE.has(activeSection);

  const {
    data: licenseStats,
    isLoading: isLicensesQueryLoading,
    error: licensesQueryError,
    refetch: refetchLicenseStats,
  } = useQuery({
    ...getNonRegistrantLicenseStatsOptions({
      query: {
        license_type_ids:
          selectedLicenseTypes.length > 0 ? selectedLicenseTypes : undefined,
        ...regionQueryParams(selectedRegionIds),
      },
    }),
    enabled: loadLicenseStats,
    ...dashboardListQueryOptions,
  });

  // Unregistered properties query to get data for stats calculations
  const {
    data: unregisteredData,
    isLoading: isUnregisteredQueryLoading,
    error: unregisteredQueryError,
  } = useQuery({
    ...getUnregisteredPropertiesOptions({
      query: {
        jurisdiction_ids: jurisdictionData ? [jurisdictionData.id] : [],
        ...regionQueryParams(selectedRegionIds),
      },
    }),
    enabled: loadUnregistered,
    ...dashboardListQueryOptions,
  });

  // Inspections query to get data for stats calculations
  const {
    data: inspectionsData,
    isLoading: isInspectionsQueryLoading,
    error: inspectionsQueryError,
  } = useQuery({
    ...getInspectionsOptions({
      query: {
        jurisdiction_ids: jurisdictionData ? [jurisdictionData.id] : [],
        include_deleted: false,
        ...regionQueryParams(selectedRegionIds),
      },
    }),
    enabled: loadInspections,
    ...dashboardListQueryOptions,
  });

  // Filter license type configurations based on selected license types
  const filteredLicenseTypeConfigs = useMemo(() => {
    if (!licenseTypeConfigsData) return [];

    // If no license types are selected, include all configurations
    if (selectedLicenseTypes.length === 0) return licenseTypeConfigsData;

    // Only include configurations for the selected license types
    return licenseTypeConfigsData.filter((config) =>
      selectedLicenseTypes.includes(config.license_type_id),
    );
  }, [licenseTypeConfigsData, selectedLicenseTypes]);

  // For government users, there's always exactly one jurisdiction (their own)
  const totalJurisdictionsCount = 1;

  // Get the jurisdiction for the filter options
  const jurisdictionForFilter = jurisdictionData ? [jurisdictionData] : [];

  // Calculate unregistered properties count
  const totalUnregisteredProperties = useMemo(() => {
    if (!unregisteredData?.unregistered) return 0;

    const activeUnregistered = unregisteredData.unregistered.filter(
      (u) => !u.is_deleted,
    );

    // If no license types are selected, include all active unregistered properties
    if (selectedLicenseTypes.length === 0) return activeUnregistered.length;

    // Only include unregistered properties that match selected license types
    return activeUnregistered.filter((u) =>
      selectedLicenseTypes.includes(u.license_type.id),
    ).length;
  }, [unregisteredData?.unregistered, selectedLicenseTypes]);

  // Calculate active inspections count (INSPECTION_PENDING status)
  const activeInspectionsCount = useMemo(() => {
    if (!inspectionsData?.inspections) return 0;
    return inspectionsData.inspections.filter(
      (inspection) => inspection.status === "INSPECTION_PENDING",
    ).length;
  }, [inspectionsData?.inspections]);

  const inspectionsPendingReviewCount = useMemo(() => {
    if (!inspectionsData?.inspections) return 0;
    return inspectionsData.inspections.filter(
      (inspection) => inspection.status === "COMMITTED",
    ).length;
  }, [inspectionsData?.inspections]);

  const remittanceQuery = useMemo(
    () => ({
      license_type_ids:
        selectedLicenseTypes.length > 0 ? selectedLicenseTypes : undefined,
      count: "12",
    }),
    [selectedLicenseTypes],
  );
  const { rows: remittanceRows } = useStreamingNdjson<RemittanceTrendRow>({
    path: "/government/portal/trends/remittance",
    query: remittanceQuery,
    enabled: loadRemittance,
  });
  const totalYTDHeraRemittance = useMemo(() => {
    const currentYear = new Date().getUTCFullYear();
    const isCurrentYearBucket = (bucket: string) => {
      const maybeYear = Number.parseInt(bucket.slice(-4), 10);
      return Number.isFinite(maybeYear) && maybeYear === currentYear;
    };
    return remittanceRows.reduce(
      (sum, row) =>
        isCurrentYearBucket(row.bucket)
          ? sum +
            (row.hera_registration_remittance ?? 0) +
            (row.hera_late_fee_remittance ?? 0) +
            (row.hera_inspection_remittance ?? 0)
          : sum,
      0,
    );
  }, [remittanceRows]);

  const ordinanceListRowCount = useMemo(() => {
    if (!jurisdictionData || !licenseTypeConfigsData) return 0;
    let rows = buildOrdinanceRowsFromGovernmentData(
      jurisdictionData,
      licenseTypeConfigsData,
    );
    if (selectedLicenseTypes.length > 0) {
      const ltSet = new Set(selectedLicenseTypes);
      rows = rows.filter((r) => ltSet.has(r.licenseTypeId));
    }
    return rows.length;
  }, [jurisdictionData, licenseTypeConfigsData, selectedLicenseTypes]);

  return {
    // Data
    jurisdictionsData: jurisdictionForFilter,
    unregisteredData,
    filteredJurisdictions: jurisdictionForFilter,
    licenseTypeConfigsData,
    filteredLicenseTypeConfigs,

    // Loading states
    isJurisdictionsQueryLoading: isJurisdictionQueryLoading,
    isLicensesQueryLoading,
    isUnregisteredQueryLoading,
    isInspectionsQueryLoading,

    // Error states
    jurisdictionsQueryError: jurisdictionQueryError,
    licensesQueryError,
    unregisteredQueryError,
    inspectionsQueryError,

    // Computed stats
    pendingReviewCount: licenseStats?.pending_review_count ?? 0,
    disputedCount: licenseStats?.disputed_count ?? 0,
    refundedCount: licenseStats?.refunded_count ?? 0,
    totalYTDPaidAmount: Number.parseFloat(
      licenseStats?.total_ytd_paid_amount ?? "0",
    ),
    totalRegistrationsCount: licenseStats?.total_registrations ?? 0,
    totalJurisdictionsCount,
    totalUnregisteredProperties,
    activeInspectionsCount,
    inspectionsPendingReviewCount,
    totalYTDHeraRemittance,
    ordinanceListRowCount,
    refetchLicenseStats,

    isDashboardShellLoading:
      isJurisdictionQueryLoading ||
      (loadUnregistered && isUnregisteredQueryLoading) ||
      (loadInspections && isInspectionsQueryLoading),
  };
}
